import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Typography, Button, Spin, Alert } from 'antd'
import { CreditCardOutlined, BankOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { env } from '../config/environment'
import { getPublicInvoicePayContext, isCardOnlyCheckout } from '../utils/publicInvoicePayContext'
import {
  PublicPaymentMethodChooser,
  type PublicPaymentMethod,
} from '../components/publicPay/PublicPaymentMethodChooser'
import { PublicPayInvoiceSummary } from '../components/publicPay/PublicPayInvoiceSummary'
import { HelcimPayCheckout } from '../components/payments/HelcimPayCheckout'
import { FinixPayCheckout } from '../components/payments/FinixPayCheckout'
import { BanccardPayCheckout } from '../components/payments/BanccardPayCheckout'
import { AffirmFinancingBlock } from '../components/financing/AffirmFinancingBlock'
import {
  amountDollarsToCents,
  banccardAchEnabled,
  finixAchEnabled,
  helcimAchEnabled,
  resolvePreferredCardProcessor,
  type BusinessIntegrations,
} from '@serviceflow/shared'

const { Text } = Typography

const API_URL = env.apiBaseUrl.replace(/\/api$/, '')

function listStripeBlobsFromBusinessSettings(settings: Record<string, unknown>): Record<string, unknown>[] {
  const integrations = settings.integrations as Record<string, unknown> | undefined
  const out: Record<string, unknown>[] = []
  for (const x of [integrations?.stripe, settings.stripeConfig, settings.stripe]) {
    if (x && typeof x === 'object' && !Array.isArray(x)) out.push(x as Record<string, unknown>)
  }
  return out
}

function stripeBlobWithSecretKey(settings: Record<string, unknown>): Record<string, unknown> | null {
  for (const b of listStripeBlobsFromBusinessSettings(settings)) {
    const sk = b.secretKey
    if (typeof sk === 'string' && sk.trim().length > 0) return b
  }
  return null
}

function isAchTruthy(v: unknown): boolean {
  if (v === true || v === 1) return true
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'true' || s === '1' || s === 'yes'
  }
  return false
}

function stripeBlobSuggestsAch(block: Record<string, unknown>): boolean {
  // Opt-in only — do not infer ACH from legacy paymentMethodTypes arrays
  return isAchTruthy(block.achEnabled)
}

function isAchEnabledFromBusinessSettings(settings: Record<string, unknown>): boolean {
  if (!stripeBlobWithSecretKey(settings)) return false
  for (const blob of listStripeBlobsFromBusinessSettings(settings)) {
    if (stripeBlobSuggestsAch(blob)) return true
  }
  return false
}

export const PaymentMethodSelectPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [creatingLink, setCreatingLink] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<any>(null)
  const [selectedMethod, setSelectedMethod] = useState<PublicPaymentMethod>('card')
  const [helcimCheckout, setHelcimCheckout] = useState<{
    checkoutToken: string
    secretToken: string
  } | null>(null)
  const [banccardCheckout, setBanccardCheckout] = useState<{
    checkoutToken: string
    secretToken: string
    tokenizationKey: string
  } | null>(null)
  const [finixCheckout, setFinixCheckout] = useState<{
    checkoutToken: string
    secretToken: string
    finix: {
      applicationId: string | null
      merchantId: string
      merchantIdentityId: string | null
      environment: 'sandbox' | 'live'
      amount: number
      currency: string
      applePayEnabled?: boolean
      googlePayEnabled?: boolean
      achEnabled?: boolean
      paymentMethods?: Array<'card' | 'bank'>
    }
  } | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  useEffect(() => {
    if (token) {
      fetchData()
    } else {
      setError('Invalid payment link')
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (!data?.invoice) return
    if (isCardOnlyCheckout(data.invoice)) {
      setSelectedMethod('card')
    }
  }, [data])

  const fetchData = async () => {
    if (!token) return
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_URL}/api/public/pdf/${token}`)
      const result = await response.json()

      if (!result.success) {
        setError(result.error || 'Failed to load payment information')
        setLoading(false)
        return
      }

      if (result.type !== 'invoice') {
        setError('Payment is only available for invoices')
        setLoading(false)
        return
      }

      setData(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load payment information')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePayment = async () => {
    if (!token) return
    try {
      setCreatingLink(true)
      setError(null)
      const response = await fetch(`${API_URL}/api/public/pdf/${token}/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethod: selectedMethod,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
        }),
      })

      const result = await response.json()

      if (result.success && result.processor === 'helcim' && result.checkoutToken && result.secretToken) {
        setHelcimCheckout({
          checkoutToken: result.checkoutToken,
          secretToken: result.secretToken,
        })
      } else if (
        result.success &&
        result.processor === 'banccard' &&
        result.checkoutToken &&
        result.secretToken &&
        result.publishableKey
      ) {
        setBanccardCheckout({
          checkoutToken: result.checkoutToken,
          secretToken: result.secretToken,
          tokenizationKey: result.publishableKey,
        })
      } else if (
        result.success &&
        result.processor === 'finix' &&
        result.checkoutToken &&
        result.secretToken &&
        result.finix
      ) {
        setFinixCheckout({
          checkoutToken: result.checkoutToken,
          secretToken: result.secretToken,
          finix: result.finix,
        })
      } else if (result.success && result.url) {
        window.location.href = result.url
      } else {
        throw new Error(result.error || result.message || 'Failed to create payment link')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create payment link. Please try again.')
    } finally {
      setCreatingLink(false)
    }
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f5f5f5',
        }}
      >
        <Spin size="large" tip="Loading payment options..." />
      </div>
    )
  }

  if (error && !data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f5f5f5',
          padding: 24,
        }}
      >
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          action={<Button onClick={() => navigate(-1)}>Go Back</Button>}
        />
      </div>
    )
  }

  const { invoice, business } = data || {}
  const { payAmount, isPaid } = getPublicInvoicePayContext(invoice)

  const settings =
    typeof business?.settings === 'string'
      ? JSON.parse(business?.settings || '{}')
      : business?.settings || {}
  const feePercent = settings.creditCardProcessingFeePercent ?? 0
  const cardOnly = isCardOnlyCheckout(invoice)
  const integrations = (settings.integrations || {}) as BusinessIntegrations
  const preferredProcessor = resolvePreferredCardProcessor(integrations)
  const achEnabled =
    preferredProcessor === 'finix'
      ? finixAchEnabled(integrations)
      : preferredProcessor === 'helcim'
        ? helcimAchEnabled(integrations)
        : preferredProcessor === 'banccard'
          ? banccardAchEnabled(integrations)
        : preferredProcessor === 'stripe'
          ? isAchEnabledFromBusinessSettings(settings)
          : false

  if (paymentSuccess) {
    return (
      <div style={{ maxWidth: 500, margin: '0 auto', padding: 24, minHeight: '100vh', background: '#f5f5f5' }}>
        <Alert type="success" message="Payment Successful" description="Your payment has been processed." showIcon />
      </div>
    )
  }

  if (isPaid) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f5f5f5',
          padding: 24,
        }}
      >
        <Alert
          type="success"
          message="Invoice Already Paid"
          description="This invoice has already been paid."
          showIcon
          action={<Button onClick={() => navigate(-1)}>Go Back</Button>}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: '16px 16px 32px',
        minHeight: '100vh',
        background: '#f5f5f5',
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
        Back
      </Button>

      <PublicPayInvoiceSummary invoice={invoice} business={business} />

      <Card title={cardOnly ? 'Pay with card' : 'Select payment method'} style={{ marginBottom: 24, borderRadius: 12 }}>
        {!helcimCheckout && !finixCheckout && !banccardCheckout && token && (
          <AffirmFinancingBlock
            amountCents={amountDollarsToCents(payAmount)}
            pdfToken={token}
            source="payment_method_select"
            compact
          />
        )}
        {banccardCheckout && token ? (
          <BanccardPayCheckout
            checkoutToken={banccardCheckout.checkoutToken}
            secretToken={banccardCheckout.secretToken}
            tokenizationKey={banccardCheckout.tokenizationKey}
            amountLabel={`Pay $${payAmount.toFixed(2)} for Invoice #${invoice?.invoiceNumber}`}
            preferredPayment={selectedMethod === 'ach' ? 'check' : 'creditcard'}
            allowAch={achEnabled && selectedMethod !== 'card'}
            onValidate={async (payload) => {
              const validateRes = await fetch(`${API_URL}/api/public/pdf/${token}/validate-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
              const validateJson = await validateRes.json()
              if (!validateJson.success) {
                throw new Error(validateJson.error || 'Payment validation failed')
              }
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        ) : finixCheckout && token ? (
          <FinixPayCheckout
            checkoutToken={finixCheckout.checkoutToken}
            secretToken={finixCheckout.secretToken}
            finix={finixCheckout.finix}
            amountLabel={`Pay $${payAmount.toFixed(2)} for Invoice #${invoice?.invoiceNumber}`}
            apiBaseUrl={API_URL}
            pdfToken={token}
            onValidate={async (payload) => {
              const validateRes = await fetch(`${API_URL}/api/public/pdf/${token}/validate-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
              const validateJson = await validateRes.json()
              if (!validateJson.success) {
                throw new Error(validateJson.error || 'Payment validation failed')
              }
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        ) : helcimCheckout && token ? (
          <HelcimPayCheckout
            checkoutToken={helcimCheckout.checkoutToken}
            secretToken={helcimCheckout.secretToken}
            amountLabel={`Pay $${payAmount.toFixed(2)} for Invoice #${invoice?.invoiceNumber}`}
            onValidate={async (payload) => {
              const validateRes = await fetch(`${API_URL}/api/public/pdf/${token}/validate-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
              })
              const validateJson = await validateRes.json()
              if (!validateJson.success) {
                throw new Error(validateJson.error || 'Payment validation failed')
              }
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        ) : (
          <>
        <PublicPaymentMethodChooser
          value={selectedMethod}
          onChange={setSelectedMethod}
          subtotal={payAmount}
          feePercent={feePercent}
          achEnabled={achEnabled}
          cardOnly={cardOnly}
          variant="boxed"
        />

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 16, marginTop: 16 }} />
        )}

        <Button
          type="primary"
          size="large"
          block
          icon={selectedMethod === 'ach' ? <BankOutlined /> : <CreditCardOutlined />}
          onClick={handleCreatePayment}
          loading={creatingLink}
          style={{
            height: 48,
            fontSize: 16,
            marginTop: 16,
            background: selectedMethod === 'ach' ? '#52c41a' : undefined,
            borderColor: selectedMethod === 'ach' ? '#52c41a' : undefined,
          }}
        >
          {selectedMethod === 'ach' ? 'Continue with Bank Transfer' : 'Continue with Credit Card'}
        </Button>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            You will be redirected to a secure payment page.
          </Text>
        </div>
          </>
        )}
      </Card>
    </div>
  )
}
